import { Button, Stack, Stepper } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigateToAzureStoragePlanList, useNavigateToCreateNewAzureStoragePlannerAssessment } from "./AzureStoragePlannerHelpers";
import { ScreenContainer, ScreenTitleBar } from "../../layout/ScreenCommon";
import { StepConfig } from "../../../common/stepper/StepperHelpers";
import { DynamicStepper } from "../../../common/stepper/StepperComponents";
import { AzureStoragePlannerDescriptionStep } from "./AzureStoragePlannerDescriptionStep";
import { AzureStoragePlannerSelectHostStep } from "./AzureStoragePlannerSelectHostStep";
import { AzureStoragePlannerGeneratePlanStep } from "./AzureStoragePlannerGeneratePlanStep";
import { useCurrentProjectID } from "../../project/CurrentProjectState";
import { useAzureStoragePlannerState } from "./AzureStoragePlannerState";

import { OperatorView } from "../../auth/AuthenticatedViews";
import { useMountEffect } from "../../../common/hooks/hookslib";
import { useTranslation } from "react-i18next";

interface AzureStoragePlannerActionsProps {
    compact?: boolean;
}
export const AzureStoragePlannerActions: React.FC<AzureStoragePlannerActionsProps> = (p) => {
    const { t } = useTranslation("integrations");
    const goToPlanner = useNavigateToCreateNewAzureStoragePlannerAssessment();
    const goToPlanList = useNavigateToAzureStoragePlanList();
    const compact = Boolean(p.compact);

    return (
        <Stack direction={compact ? "row" : "column"} spacing={compact ? 1 : 2}>
            <OperatorView>
                <Button variant={compact ? "text" : "contained"} color={"primary"} size={compact ? "small" : "medium"} onClick={goToPlanner}>
                    {compact ? t("azurePlanner.actions.create") : t("azurePlanner.actions.createAssessment")}
                </Button>
            </OperatorView>
            <Button variant={compact ? "text" : "outlined"} color={compact ? "primary" : "neutral"} size={compact ? "small" : "medium"} onClick={goToPlanList}>
                {compact ? t("azurePlanner.actions.view") : t("azurePlanner.actions.viewAssessments")}
            </Button>
        </Stack>
    );
};

// ======================
// AzureStoragePlannerWizard
// ======================

interface AzureStoragePlannerWizardProps {}

export const AzureStoragePlannerWizard: React.FC<AzureStoragePlannerWizardProps> = (p) => {
    const { t } = useTranslation("integrations");
    const projectId = useCurrentProjectID();
    const setProjectId = useAzureStoragePlannerState((state) => state.setProjectId);

    useMountEffect(() => {
        setProjectId(projectId);
    });

    const getStoragePlannerStepConfigs = (): StepConfig[] => {
        const descriptionStepConfig: StepConfig = {
            id: "description",
            label: t("azurePlanner.wizard.steps.description"),
            renderer: () => <AzureStoragePlannerDescriptionStep />,
        };

        const sourceHostSelectionStepConfig: StepConfig = {
            id: "sourceHost",
            label: t("azurePlanner.wizard.steps.selectHost"),
            renderer: () => <AzureStoragePlannerSelectHostStep />,
        };

        const generateConsumptionPlannerStepConfig: StepConfig = {
            id: "generatePlanner",
            label: t("azurePlanner.wizard.steps.generate"),
            renderer: () => <AzureStoragePlannerGeneratePlanStep />,
        };

        return [descriptionStepConfig, sourceHostSelectionStepConfig, generateConsumptionPlannerStepConfig];
    };

    return (
        <ScreenContainer>
            <ScreenTitleBar title={t("azurePlanner.wizard.title")} />
            <DynamicStepper
                stepperProps={{
                    activeStep: 0,
                    stepConfigs: getStoragePlannerStepConfigs(),
                    startingStep: 0,
                    totalSteps: getStoragePlannerStepConfigs().length,
                }}
                orientation={"horizontal"}
            />
        </ScreenContainer>
    );
};
